import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function toTrimmedString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function getFiniteNumber(value: unknown): number | null {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

export function normalizeRealtimeUserIds(userIds: Array<number | string | null | undefined>): number[] {
  return Array.from(new Set(
    userIds
      .map((userId) => Number(userId))
      .filter((userId) => Number.isFinite(userId) && userId > 0),
  ));
}

export function formatAppointmentWorkflowStatusLabel(value: string): string {
  switch (normalizeWorkflowStatusKey(value)) {
    case 'on_route':
    case 'en_route':
      return 'On Route / Travelling';
    case 'in_progress':
    case 'checked_in':
    case 'working':
      return 'In Progress / Working';
    default: {
      const normalizedValue = normalizeWorkflowStatusKey(value) || value;
      return normalizedValue.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }
  }
}

export function describeAppointmentOverrideBlockers(gateData: unknown): string[] {
  if (!isRecord(gateData)) {
    return [];
  }

  const blockers: string[] = [];
  const formsGate = isRecord(gateData.forms_gate) ? gateData.forms_gate : null;
  const formsGateReleased = Boolean(formsGate?.gate_released);
  const formsGateEnabled = Boolean(formsGate?.enabled) || Boolean(formsGate?.enforced);
  const formsTotal = getFiniteNumber(formsGate?.total_required_forms) ?? getFiniteNumber(formsGate?.total) ?? 0;
  const formsCompleted = getFiniteNumber(formsGate?.completed_required_forms) ?? getFiniteNumber(formsGate?.completed) ?? 0;
  const formsRemaining = Math.max(formsTotal - formsCompleted, 0);

  if (formsGateEnabled && !formsGateReleased && formsRemaining > 0) {
    blockers.push(`Forms gate: ${formsRemaining} required form${formsRemaining === 1 ? '' : 's'} still outstanding (${formsCompleted}/${formsTotal} complete).`);
  }

  const processGates = Array.isArray(gateData.process_gates)
    ? gateData.process_gates.filter(isRecord)
    : [];

  processGates.forEach((gate) => {
    const blocksTargetStatus = Boolean(gate.blocks_target_status) || Boolean(gate.is_blocked);
    const gateReleased = Boolean(gate.gate_released);
    if (!blocksTargetStatus || gateReleased) {
      return;
    }

    const summary = isRecord(gate.summary) ? gate.summary : null;
    const totalSteps = getFiniteNumber(summary?.total_required_steps) ?? getFiniteNumber(summary?.required_steps) ?? getFiniteNumber(summary?.total) ?? 0;
    const completedSteps = getFiniteNumber(summary?.completed_required_steps) ?? getFiniteNumber(summary?.completed_steps) ?? getFiniteNumber(summary?.completed) ?? 0;
    const remainingSteps = Math.max(totalSteps - completedSteps, 0);
    const templateName = toTrimmedString(gate.template_name)
      || toTrimmedString(gate.name)
      || 'Process gate';

    blockers.push(
      remainingSteps > 0
        ? `${templateName}: ${remainingSteps} required step${remainingSteps === 1 ? '' : 's'} still incomplete (${completedSteps}/${totalSteps} complete).`
        : `${templateName}: this process gate is still actively blocking the target status.`,
    );
  });

  return blockers;
}

export function buildAppointmentRealtimeFanoutChannels(
  clientId: number | string | null | undefined,
  targetUserIds: Array<number | string | null | undefined>,
): string[] {
  const numericClientId = Number(clientId);
  if (!Number.isFinite(numericClientId) || numericClientId <= 0) {
    return [];
  }

  return [
    `operations:client:${numericClientId}`,
    ...normalizeRealtimeUserIds(targetUserIds).map((userId) => `tasks:user:${userId}:client:${numericClientId}`),
  ];
}

export function extractRealtimeAppointmentStatusOverride(data: unknown): string | null {
  if (!isRecord(data)) {
    return null;
  }

  const payload = isRecord(data.payload) ? data.payload : data;
  return toTrimmedString(payload.new_status) || toTrimmedString(payload.status) || null;
}
